// Cupons nativos (2026-10-16): regras e contas em centavos.
//
// Módulo PURO (sem "server-only", sem banco, sem Date.now implícito): roda
// no servidor (carrinho, checkout, admin) e também no navegador (o resumo do
// checkout mostra o mesmo total que o banco vai cobrar). Toda conta é em
// inteiros de centavos; nada de ponto flutuante com dinheiro.
//
// A AUTORIDADE do valor cobrado é a função do banco submit_native_checkout_v3
// (supabase/migrations/20261016110000_native_coupons.sql), que refaz as
// mesmas contas no instante em que cria o pedido e recusa (COUPON_STALE) se
// o valor calculado aqui for diferente. Os testes conferem que as duas
// implementações batem (tests/nativeCoupons*.test.mjs).
//
// Regras de dinheiro (decisão do dono 2026-10-16):
//  1. Base do cupom = valor das linhas ELEGÍVEIS já com descontos de linha
//     (preço promocional já está no preço efetivo). Frete nunca entra na base
//     de cupom percentual ou fixo.
//  2. Percentual: round_half_up(base * bps / 10000). Fixo: min(valor, base).
//     Os dois limitados pelo teto (max_discount) e nunca acima da base.
//  3. Frete grátis: desconto = frete escolhido (limitado pelo teto, se houver).
//  4. Pix/boleto (10%/5%) — modo "after_coupon" (padrão): o cupom entra
//     primeiro e o desconto da forma de pagamento é calculado sobre o que
//     sobra dos produtos: round_half_up((produtos - descontos de linha -
//     cupom nos produtos) * bps / 10000). Frete fora da base (como hoje).
//     Modo "not_combinable": no Pix/boleto vale SÓ o maior dos dois descontos
//     (cupom inteiro, contando frete grátis, contra o desconto da forma de
//     pagamento sem cupom); empate = cupom. Cartão/carteiras: só o cupom.
//  5. Divisão do desconto do cupom pelas linhas elegíveis (para estorno e
//     relatório): proporcional ao valor da linha, centavos que sobram vão
//     para as linhas com maior resto (empate: menor line_number).

export type CouponKind = "percentage" | "fixed_cart" | "free_shipping";
export type CouponPixMode = "after_coupon" | "not_combinable";
export type CouponStatus = "active" | "disabled";

export const COUPON_KINDS: readonly CouponKind[] = ["percentage", "fixed_cart", "free_shipping"];
export const COUPON_PIX_MODES: readonly CouponPixMode[] = ["after_coupon", "not_combinable"];

export const COUPON_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,49}$/;
export const COUPON_MAX_TARGETS = 500;

export interface CouponRule {
  id: string;
  version: number;
  code: string;
  description: string | null;
  kind: CouponKind;
  /** Percentual em pontos-base (10% = 1000). Só para "percentage". */
  percentBps: number | null;
  /** Valor fixo em centavos. Só para "fixed_cart". */
  amountMinor: number | null;
  minSubtotalMinor: number | null;
  maxDiscountMinor: number | null;
  startsAt: Date | null;
  endsAt: Date | null;
  status: CouponStatus;
  usageLimitTotal: number | null;
  usageLimitPerCustomer: number | null;
  includeProductIds: readonly string[];
  excludeProductIds: readonly string[];
  includeCategoryIds: readonly string[];
  excludeCategoryIds: readonly string[];
  includeBrandIds: readonly string[];
  excludeBrandIds: readonly string[];
  excludeSaleItems: boolean;
  pixMode: CouponPixMode;
  firstPurchaseOnly: boolean;
}

export interface CouponLine {
  /** Id estável da linha (checkout_session_items.id no checkout; variante no carrinho). */
  lineId: string;
  lineNumber: number;
  productId: string;
  /** Categorias do produto INCLUINDO as categorias-mãe (subcategoria herda a regra). */
  categoryIds: readonly string[];
  brandId: string | null;
  /** Valor da linha já com desconto de linha (centavos, >= 0). */
  netMinor: number;
  /** Preço efetivo menor que o preço de lista (item em promoção). */
  onSale: boolean;
  /** Item com o preço especial do order bump: nunca recebe cupom (2026-10-26). */
  orderBump?: boolean;
}

export type CouponRejection =
  | "COUPON_DISABLED"
  | "COUPON_NOT_STARTED"
  | "COUPON_EXPIRED"
  | "COUPON_MIN_SUBTOTAL"
  | "COUPON_NO_ELIGIBLE_ITEMS"
  | "COUPON_NO_DISCOUNT";

export interface CouponLineAllocation {
  lineId: string;
  lineNumber: number;
  discountMinor: number;
}

export interface CouponEvaluationApplied {
  ok: true;
  eligibleLineIds: string[];
  eligibleBaseMinor: number;
  /** Desconto do cupom nos produtos (0 para frete grátis). */
  itemDiscountMinor: number;
  allocations: CouponLineAllocation[];
  freeShipping: boolean;
  /** Desconto no frete; null quando o frete ainda não é conhecido. */
  shippingDiscountMinor: number | null;
}

export interface CouponEvaluationRejected {
  ok: false;
  reason: CouponRejection;
  /** Para COUPON_MIN_SUBTOTAL: quanto falta (centavos). */
  missingMinor?: number;
}

export type CouponEvaluation = CouponEvaluationApplied | CouponEvaluationRejected;

function assertSafeMinor(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0) throw new RangeError(`${label} inválido`);
  return value;
}

/** Arredonda (valor * bps / 10000) para o centavo, metade para cima (igual ao round() do Postgres para numeric >= 0). */
export function applyBasisPointsHalfUp(valueMinor: number, bps: number): number {
  assertSafeMinor(valueMinor, "valor");
  if (!Number.isInteger(bps) || bps < 0 || bps > 10000) throw new RangeError("bps inválido");
  // BigInt evita estouro de Number para valores grandes (valor * bps).
  const product = BigInt(valueMinor) * BigInt(bps);
  return Number((product + BigInt(5000)) / BigInt(10000));
}

export function normalizeCouponCode(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidCouponCode(raw: string): boolean {
  return COUPON_CODE_PATTERN.test(raw.trim());
}

function intersects(left: readonly string[], right: readonly string[]): boolean {
  if (left.length === 0 || right.length === 0) return false;
  const set = new Set(left);
  return right.some((value) => set.has(value));
}

/**
 * Linha participa do cupom? Exclusões sempre vencem. Inclusões: se alguma
 * lista de inclusão (produtos, categorias, marcas) estiver preenchida, a
 * linha precisa bater com PELO MENOS UMA delas (mesma semântica "ou" do
 * WooCommerce entre produtos e categorias).
 */
export function isCouponLineEligible(rule: CouponRule, line: CouponLine): boolean {
  if (line.orderBump) return false;
  if (rule.excludeSaleItems && line.onSale) return false;
  if (rule.excludeProductIds.includes(line.productId)) return false;
  if (intersects(rule.excludeCategoryIds, line.categoryIds)) return false;
  if (line.brandId && rule.excludeBrandIds.includes(line.brandId)) return false;
  const hasIncludes = rule.includeProductIds.length > 0 || rule.includeCategoryIds.length > 0 || rule.includeBrandIds.length > 0;
  if (!hasIncludes) return true;
  if (rule.includeProductIds.includes(line.productId)) return true;
  if (intersects(rule.includeCategoryIds, line.categoryIds)) return true;
  if (line.brandId && rule.includeBrandIds.includes(line.brandId)) return true;
  return false;
}

/**
 * Divide `totalMinor` entre as linhas proporcionalmente a netMinor (maior
 * resto). Soma das partes = totalMinor; nenhuma parte passa do valor da linha
 * quando totalMinor <= soma das linhas.
 */
export function allocateProportionally(
  lines: ReadonlyArray<{ lineId: string; lineNumber: number; netMinor: number }>,
  totalMinor: number,
): CouponLineAllocation[] {
  assertSafeMinor(totalMinor, "total");
  const base = lines.reduce((sum, line) => sum + BigInt(assertSafeMinor(line.netMinor, "linha")), BigInt(0));
  if (lines.length === 0 || totalMinor === 0 || base === BigInt(0)) {
    return lines.map((line) => ({ lineId: line.lineId, lineNumber: line.lineNumber, discountMinor: 0 }));
  }
  const total = BigInt(totalMinor);
  const shares = lines.map((line, index) => {
    const numerator = total * BigInt(line.netMinor);
    return { index, line, floor: numerator / base, remainder: numerator % base };
  });
  let leftover = total - shares.reduce((sum, share) => sum + share.floor, BigInt(0));
  const order = [...shares].sort((left, right) => {
    if (left.remainder !== right.remainder) return left.remainder > right.remainder ? -1 : 1;
    return left.line.lineNumber - right.line.lineNumber;
  });
  const extra = new Map<number, bigint>();
  for (const share of order) {
    if (leftover <= BigInt(0)) break;
    extra.set(share.index, BigInt(1));
    leftover -= BigInt(1);
  }
  return shares.map((share) => ({
    lineId: share.line.lineId,
    lineNumber: share.line.lineNumber,
    discountMinor: Number(share.floor + (extra.get(share.index) ?? BigInt(0))),
  }));
}

export interface EvaluateCouponInput {
  rule: CouponRule;
  lines: readonly CouponLine[];
  /** Frete escolhido em centavos; null = ainda não escolhido. 0 = retirada/grátis. */
  shippingMinor: number | null;
  now: Date;
}

/** Valida o cupom contra o carrinho/checkout e calcula o desconto. Limites de uso ficam no banco. */
export function evaluateCoupon({ rule, lines, shippingMinor, now }: EvaluateCouponInput): CouponEvaluation {
  if (rule.status !== "active") return { ok: false, reason: "COUPON_DISABLED" };
  if (rule.startsAt && now.getTime() < rule.startsAt.getTime()) return { ok: false, reason: "COUPON_NOT_STARTED" };
  if (rule.endsAt && now.getTime() >= rule.endsAt.getTime()) return { ok: false, reason: "COUPON_EXPIRED" };

  const itemsNet = lines.reduce((sum, line) => sum + assertSafeMinor(line.netMinor, "linha"), 0);
  if (rule.minSubtotalMinor !== null && itemsNet < rule.minSubtotalMinor) {
    return { ok: false, reason: "COUPON_MIN_SUBTOTAL", missingMinor: rule.minSubtotalMinor - itemsNet };
  }

  const eligible = [...lines]
    .filter((line) => line.netMinor > 0 && isCouponLineEligible(rule, line))
    .sort((left, right) => left.lineNumber - right.lineNumber);
  if (eligible.length === 0) return { ok: false, reason: "COUPON_NO_ELIGIBLE_ITEMS" };
  const base = eligible.reduce((sum, line) => sum + line.netMinor, 0);

  let itemDiscount = 0;
  if (rule.kind === "percentage") itemDiscount = applyBasisPointsHalfUp(base, rule.percentBps ?? 0);
  else if (rule.kind === "fixed_cart") itemDiscount = Math.min(rule.amountMinor ?? 0, base);
  if (rule.kind !== "free_shipping" && rule.maxDiscountMinor !== null) itemDiscount = Math.min(itemDiscount, rule.maxDiscountMinor);
  itemDiscount = Math.min(itemDiscount, base);

  let shippingDiscount: number | null = 0;
  if (rule.kind === "free_shipping") {
    if (shippingMinor === null) shippingDiscount = null;
    else {
      const shipping = assertSafeMinor(shippingMinor, "frete");
      shippingDiscount = rule.maxDiscountMinor !== null ? Math.min(shipping, rule.maxDiscountMinor) : shipping;
    }
  }

  if (rule.kind !== "free_shipping" && itemDiscount === 0) return { ok: false, reason: "COUPON_NO_DISCOUNT" };

  return {
    ok: true,
    eligibleLineIds: eligible.map((line) => line.lineId),
    eligibleBaseMinor: base,
    itemDiscountMinor: itemDiscount,
    allocations: allocateProportionally(eligible, itemDiscount),
    freeShipping: rule.kind === "free_shipping",
    shippingDiscountMinor: shippingDiscount,
  };
}

export interface OrderTotalsWithCouponInput {
  /** Soma de line_subtotal (preço efetivo * quantidade). */
  itemsSubtotalMinor: number;
  /** Soma de line_discount (hoje sempre 0 no checkout nativo). */
  lineDiscountMinor?: number;
  shippingMinor: number;
  couponItemDiscountMinor: number;
  couponShippingDiscountMinor: number;
  pixMode: CouponPixMode;
  /** Desconto da forma de pagamento em pontos-base (Pix 1000, boleto 500, cartão 0). */
  paymentDiscountBps: number;
}

export interface OrderTotalsWithCoupon {
  /** false = no modo "not_combinable" o desconto do Pix/boleto foi maior e o cupom não entra. */
  couponApplied: boolean;
  couponItemDiscountMinor: number;
  couponShippingDiscountMinor: number;
  paymentDiscountMinor: number;
  /** bps gravado em orders.payment_discount_bps (0 quando o cupom não cumulativo venceu). */
  appliedPaymentDiscountBps: number;
  /** Desconto total do pedido (linha + cupom + forma de pagamento). */
  discountTotalMinor: number;
  grandTotalMinor: number;
}

/** Mesma conta de submit_native_checkout_v3 (inclusive a regra Pix x cupom). */
export function computeOrderTotalsWithCoupon(input: OrderTotalsWithCouponInput): OrderTotalsWithCoupon {
  const subtotal = assertSafeMinor(input.itemsSubtotalMinor, "subtotal");
  const lineDiscount = assertSafeMinor(input.lineDiscountMinor ?? 0, "desconto de linha");
  const shipping = assertSafeMinor(input.shippingMinor, "frete");
  const itemsNet = Math.max(subtotal - lineDiscount, 0);
  let couponItem = Math.min(assertSafeMinor(input.couponItemDiscountMinor, "cupom"), itemsNet);
  let couponShipping = Math.min(assertSafeMinor(input.couponShippingDiscountMinor, "cupom frete"), shipping);
  const bps = input.paymentDiscountBps;
  let couponApplied = couponItem + couponShipping > 0;
  let paymentDiscount: number;
  let appliedBps = bps;

  if (couponApplied && bps > 0 && input.pixMode === "not_combinable") {
    const paymentAlone = applyBasisPointsHalfUp(itemsNet, bps);
    if (paymentAlone > couponItem + couponShipping) {
      couponApplied = false;
      couponItem = 0;
      couponShipping = 0;
      paymentDiscount = paymentAlone;
    } else {
      paymentDiscount = 0;
      appliedBps = 0;
    }
  } else {
    paymentDiscount = applyBasisPointsHalfUp(itemsNet - couponItem, bps);
  }

  const discountTotal = lineDiscount + couponItem + couponShipping + paymentDiscount;
  return {
    couponApplied,
    couponItemDiscountMinor: couponItem,
    couponShippingDiscountMinor: couponShipping,
    paymentDiscountMinor: paymentDiscount,
    appliedPaymentDiscountBps: appliedBps,
    discountTotalMinor: discountTotal,
    grandTotalMinor: subtotal - discountTotal + shipping,
  };
}

function formatBRL(minor: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(minor / 100);
}

function formatDateBR(value: Date): string {
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric" }).format(value);
}

/** Mensagens ao cliente (pt-BR) para os motivos de recusa do cupom, inclusive os que só o banco detecta. */
export function couponRejectionMessage(reason: string, context: { missingMinor?: number; startsAt?: Date | null } = {}): string {
  switch (reason) {
    case "COUPON_NOT_FOUND":
      return "Cupom inválido ou inexistente.";
    case "COUPON_DISABLED":
      return "Este cupom não está mais ativo.";
    case "COUPON_NOT_STARTED":
      return context.startsAt ? `Este cupom vale a partir de ${formatDateBR(context.startsAt)}.` : "Este cupom ainda não está valendo.";
    case "COUPON_EXPIRED":
      return "Este cupom expirou.";
    case "COUPON_MIN_SUBTOTAL":
      return context.missingMinor && context.missingMinor > 0
        ? `Faltam ${formatBRL(context.missingMinor)} em produtos para usar este cupom.`
        : "O carrinho não atinge o valor mínimo deste cupom.";
    case "COUPON_NO_ELIGIBLE_ITEMS":
      return "Nenhum produto do carrinho participa deste cupom.";
    case "COUPON_NO_DISCOUNT":
      return "Este cupom não gera desconto nesta compra.";
    case "COUPON_USAGE_LIMIT_REACHED":
      return "Este cupom atingiu o limite de usos.";
    case "COUPON_CUSTOMER_LIMIT_REACHED":
      return "Você já usou este cupom o número máximo de vezes.";
    case "COUPON_FIRST_PURCHASE_ONLY":
      return "Este cupom vale só para a primeira compra.";
    default:
      return "Não foi possível aplicar este cupom.";
  }
}
